import type { FC } from 'react'

import { useEffect, useRef, useState } from 'react'
import { formatNumber } from '@/lib/format'

interface CountUpProps {
    value: number
    decimals?: number
    prefix?: string
    suffix?: string
    duration?: number
    className?: string
}

/**
 * Counts up to a number when it scrolls into view. The server renders the final value, so
 * numbers already on screen at load never flicker.
 */
export const CountUp: FC<CountUpProps> = ({ value, decimals = 0, prefix = '', suffix = '', duration = 1400, className }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const [display, setDisplay] = useState(value)

    useEffect(() => {
        const element = ref.current

        if (!element || !('IntersectionObserver' in window)) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const rect = element.getBoundingClientRect()
        const onScreen = rect.top < window.innerHeight && rect.bottom > 0

        if (onScreen) return

        let frameId = 0

        setDisplay(0)

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return

            observer.disconnect()

            const start = performance.now()

            const tick = (now: number) => {
                const t = Math.min(1, (now - start) / duration)
                const eased = 1 - Math.pow(1 - t, 4)

                setDisplay(value * eased)

                if (t < 1) frameId = requestAnimationFrame(tick)
            }

            frameId = requestAnimationFrame(tick)
        }, { threshold: 0.4 })

        observer.observe(element)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frameId)
        }
    }, [value, duration])

    return (
        <span ref={ref} className={className}>
            {prefix}
            {formatNumber(display, decimals)}
            {suffix}
        </span>
    )
}
